import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { TypedMemo } from '../../../common/types';
import { outreachMessages } from '../outreachMessages';
import type { PeopleRow as PeopleRowData } from '../outreachTypes';
import { PitchKindCover } from './PitchKindCover';
import { peopleRowCopy, plannedWhen } from './peopleRowCopy';
import { PlannedStateCell } from './upNext/PlannedStateCell';
import { FitRing } from './people/FitRing';
import { PitchTrack } from './people/PitchTrack';
import { pitchProgressOf } from './people/pitchProgress';
import { peopleMessages as messages } from './peopleMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the People list and the details panel */
import styles from './people/People.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PeopleRowProps {
  row: PeopleRowData;
  /** The details panel shows this row. */
  open: boolean;
  onOpen: (row: PeopleRowData) => void;
  /** The Today card (design t5): no "why" column. */
  compact?: boolean;
}

/**
 * One person of the People list (design outreach-v2 t1). The whole row opens the details panel
 * (t2-t4): there is no "Pitch" link and no block button on the row. The row shows who, why Plug
 * picked them (the fit ring and the reasons as one line) and the status with its four steps. A
 * planned row shows the state of its pitch (Plug writes it, scheduled, sending).
 */
const PeopleRowComponent: React.FC<PeopleRowProps> = ({ row, open, onOpen, compact = false }) => {
  const intl = useIntl();
  const copy = useMemo(() => peopleRowCopy(intl, row), [intl, row]);
  const isPlanned = row.result === 'scheduled';
  const progress = pitchProgressOf(row.result);
  const why = row.why.join(' · ');
  return (
    <button
      type="button"
      className={clsx(styles.row, compact && styles.todayRow, open && styles.rowOpen)}
      onClick={() => onOpen(row)}
      title={intl.formatMessage(messages.openDetails, { name: copy.title })}
      data-testid="people-row"
      data-result={row.result}
    >
      <PitchKindCover coverUrl={row.coverUrl} kind={row.kind} />
      <span className={styles.who}>
        <b className={styles.name} data-testid="people-row-title">
          {copy.title}
        </b>
        <small className={styles.sub}>{copy.sub}</small>
      </span>
      {!compact && (
        <span className={styles.why}>
          {row.fit !== null && (
            <FitRing score={row.fit} label={intl.formatMessage(outreachMessages.fit, { score: row.fit })} />
          )}
          {why !== '' && <span className={styles.whyText}>{why}</span>}
        </span>
      )}
      <span className={styles.status}>
        {isPlanned && row.plannedState !== null ? (
          <PlannedStateCell state={row.plannedState} until={plannedWhen(intl, row)} left inline />
        ) : (
          <>
            <span className={clsx(styles.statusLine, styles[`tone-${progress.tone}`])}>{copy.result.text}</span>
            <PitchTrack progress={progress} />
          </>
        )}
      </span>
      <FontAwesomeIcon icon={faChevronRight} className={styles.chevron} aria-hidden />
    </button>
  );
};

export const PeopleRow = TypedMemo(PeopleRowComponent);
